import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Typography } from '@mui/material'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import SeeMoreButton from 'src/feed/FeedComponents/SeeMoreButton'
import ScrollableFeedContainer from 'src/feed/ScrollableFeedContainer/ScrollableFeedContainer'
import useCheckMessageEditable from 'src/feed/EditChatMessage/useCheckMessageEditable'
import { useFeedContext } from 'src/feed/feedContext'
import { ChatMessageLinkType } from 'src/graphql.generated'
import { trackMarkAsUnread } from 'src/mixpanel/api'
import { useSessionUID } from 'src/session/useSession'
import { unreadBoundaryKey } from '../../api/patientConversation'
import { EntryFiles, valueOf } from '../../panels/patientHistory/EntryBody'
import { iconForHistoryType } from '../../panels/patientHistory/historyTypeIcons'
import { renderBodyForType } from '../../panels/patientHistory/renderBodyForType'
import { labelForHistoryType } from '../../panels/patientHistory/typeLabels'
import ActivityContextBody, { hasContextBody } from './ActivityContextBody'
import ActivityFactsBody from './ActivityFactsBody'
import { standardCategoryIcon } from '../../components/standardCategoryIcon'
import { useCurrentRole } from '../../currentRole'
import { chatDayLabel } from '../../format'
import { advancedColors } from '../../theme'
import { exampleFeedEvents } from '../../tour/exampleFeed'
import { ExampleFeedNotice, ExampleFeedWatermark } from '../../tour/examples'
import { TOUR_ANCHORS } from '../../tour/anchors'
import { ExampleRowsProvider, useTourExample } from '../../tour/tourExample'
import { useIsPanelPreview } from '../panelPreviewContext'
import { RoleCategoryId } from '../careTeam/types'
import { useTrackCatalog } from '../../api/useTrackCatalog'
import { AdvancedTrackDefinition } from '../../api/types'
import DayDivider from './DayDivider'
import {
  labelForCategory,
  roleCategoryIdFor,
  styleForCategory,
  styleForFeedActivity,
  viewerOwnsCategory,
} from './feedCategory'
import FeedFilterMenu from './FeedFilterMenu'
import { CHAT_HEADER_HEIGHT } from './ChatComposerLayout'
import FeedRow from './FeedRow'
import { RowAction } from './RowActionsMenu'
import { PanelPatientProvider } from '../panelContext'
import { useCareTeamRoster } from './useCareTeamRoster'
import { factsSummary, hasFactsBody } from './activityFacts'
import ChatMessageBubble from './ChatMessageBubble'
import { useLastSeenAt } from './PatientFeedProvider'
import { ChatMessageBubbleEntry, EditingMessage, ForwardedFeedItem, PendingComment } from './types'
import { ChatEventFeed, FeedEvent } from './useChatEventFeed'

// `advanced/service` caps a forward's text at 4000 characters and its context or entry at 20000 of JSON.
const MAX_FORWARD_TEXT_LENGTH = 4000
const MAX_FORWARD_JSON_LENGTH = 20000
const truncateForForward = (text: string) =>
  text.length <= MAX_FORWARD_TEXT_LENGTH ? text : `${text.slice(0, MAX_FORWARD_TEXT_LENGTH - 1)}…`
const fitsForward = (value: unknown) => JSON.stringify(value).length <= MAX_FORWARD_JSON_LENGTH

const forwardActions = (onForward?: () => void): RowAction[] =>
  onForward ? [{ label: 'Forward to care team chat', onClick: onForward }] : []

type HistoryEvent = Extract<FeedEvent, { kind: 'history' }>
type ChatEvent = Extract<FeedEvent, { kind: 'chat' }>
type ActivityEvent = Extract<FeedEvent, { kind: 'activity' }>

const forwardedDetail = (event: ActivityEvent): string | null => {
  if (event.facts) {
    return factsSummary(event.facts)
  }
  return event.detail === null ? null : truncateForForward(event.detail)
}

const commentTarget = (event: ActivityEvent): PendingComment | null => {
  if (event.activityType === 'lessonResponse' && event.responseID) {
    return {
      type: ChatMessageLinkType.Response,
      id: event.responseID,
      title: event.label,
      category: event.iconCategory,
      createdAt: event.timestamp,
    }
  }
  if (event.facts?.kind === 'overdueQuestionnaire') {
    return {
      type: ChatMessageLinkType.Lesson,
      id: event.facts.lessonID,
      title: event.label,
      category: event.iconCategory,
      createdAt: null,
    }
  }
  return null
}

const forwardedActivity = (event: ActivityEvent): ForwardedFeedItem => ({
  kind: 'activity',
  label: truncateForForward(event.label),
  detail: forwardedDetail(event),
  // The row's title and summary still travel when the answers are too long to.
  context: event.context && !fitsForward(event.context) ? { ...event.context, sections: [] } : event.context,
})

const RowHeader = ({
  icon,
  color,
  label,
  summary,
  badge,
  continued,
}: {
  icon: string
  color: string
  label: string
  summary?: string | null
  badge?: React.ReactNode
  continued?: boolean
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
    <Box component="img" src={icon} alt="" sx={{ width: 20, flexShrink: 0 }} />
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 600,
        color,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
    {summary && (
      <Typography variant="body2" sx={{ color: advancedColors.inkMuted, flexShrink: 0 }}>
        {summary}
      </Typography>
    )}
    {badge}
    {continued && (
      <Chip
        label="continued"
        size="small"
        sx={{ height: 20, fontSize: '0.7rem', color: advancedColors.inkMuted, flexShrink: 0 }}
      />
    )}
  </Box>
)

const FeedRowCard = ({
  timestamp,
  header,
  backgroundColor,
  actions,
  children,
}: {
  timestamp: string
  header: React.ReactElement
  backgroundColor: string
  actions: RowAction[]
  children?: React.ReactNode
}) => (
  <FeedRow stickToTop timestamp={timestamp} actions={actions}>
    <Box sx={{ width: '75%', maxWidth: 800, minWidth: 0 }}>
      <Accordion
        disableGutters
        elevation={0}
        // A row with nothing to show is a plain card, like Standard's overdue questionnaire.
        expanded={children ? undefined : false}
        // Collapsed bodies unmount, so a row's lookups (meditation titles) run only once it is opened.
        TransitionProps={{ timeout: 200, unmountOnExit: true }}
        sx={{
          border: `1px solid ${advancedColors.line}`,
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor,
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={children ? <ExpandMoreRoundedIcon /> : null}
          sx={{
            cursor: children ? 'pointer' : 'default',
            minHeight: 0,
            padding: '0px 20px',
            '&.Mui-expanded': { minHeight: 0 },
            '& .MuiAccordionSummary-content': { margin: '10px 0', overflow: 'hidden' },
            '& .MuiAccordionSummary-content.Mui-expanded': { margin: '10px 0' },
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>{header}</Box>
        </AccordionSummary>
        {children && <AccordionDetails sx={{ backgroundColor: advancedColors.surface }}>{children}</AccordionDetails>}
      </Accordion>
    </Box>
  </FeedRow>
)

const HistoryRow = ({ event, onForwardToCareTeam }: { event: HistoryEvent; onForwardToCareTeam?: () => void }) => {
  const style = styleForCategory('system')

  return (
    <FeedRowCard
      timestamp={event.timestamp}
      backgroundColor={advancedColors.surface}
      actions={forwardActions(onForwardToCareTeam)}
      header={
        <RowHeader
          icon={iconForHistoryType(event.entry.type)}
          color={style.fg}
          label={labelForHistoryType(event.entry.type, valueOf(event.entry))}
        />
      }
    >
      {renderBodyForType(event.entry)}
      <EntryFiles files={event.entry.files} />
    </FeedRowCard>
  )
}

const ChatRow = ({
  event,
  coachUid,
  roleFor,
  onEditMessage,
  onForwardToCareTeam,
}: {
  event: ChatEvent
  coachUid: string
  roleFor: (coachId: string) => string | null
  onEditMessage?: (message: EditingMessage) => void
  onForwardToCareTeam?: () => void
}) => {
  const isMine = event.authorCoachId === coachUid
  const sentAt = useMemo(() => new Date(event.timestamp), [event.timestamp])
  const [editable, setEditable] = useState(false)
  useCheckMessageEditable(isMine && event.channel === 'inApp' ? sentAt : undefined, setEditable)
  const entry: ChatMessageBubbleEntry = {
    id: event.id,
    text: event.text,
    createdAt: event.timestamp,
    isMine,
    authorCoachId: event.authorCoachId,
    authorRole: event.authorCoachId ? roleFor(event.authorCoachId) : null,
    authorCategory: event.category,
    channel: event.channel,
    attachments: event.attachments,
    linkedTo: event.linkedTo,
  }
  const { markAsUnread } = useFeedContext()
  const preview = useIsPanelPreview()
  const actions: RowAction[] = [
    ...(editable && onEditMessage
      ? [{ label: 'Edit', onClick: () => onEditMessage({ messageID: event.id, text: event.text, createdAt: sentAt }) }]
      : []),
    ...(event.fromPatient && !preview
      ? [
          {
            label: 'Mark as unread',
            onClick: () => {
              trackMarkAsUnread(event.channel === 'sms' ? 'sms_message' : 'chat_message')
              markAsUnread(sentAt)
            },
          },
        ]
      : []),
    ...forwardActions(onForwardToCareTeam),
  ]

  return (
    <FeedRow
      side={isMine ? 'right' : 'left'}
      timestamp={event.timestamp}
      actions={actions}
      tourAnchor={TOUR_ANCHORS.feedMessage}
    >
      <ChatMessageBubble entry={entry} inFeed />
    </FeedRow>
  )
}

// Collapsing another track's activity is for scanning, not privacy: expanding shows all of it.
const ActivityRow = ({
  event,
  viewerRole,
  tracks,
  onCommentInChat,
  onForwardToCareTeam,
}: {
  event: ActivityEvent
  viewerRole: RoleCategoryId | null
  tracks: AdvancedTrackDefinition[]
  onCommentInChat?: () => void
  onForwardToCareTeam?: () => void
}) => {
  const owned = viewerOwnsCategory(event.category, viewerRole, tracks)
  const style = styleForFeedActivity(event.category, viewerRole, tracks)
  const showsDetail = event.category === 'system' || owned
  const hasBody = event.facts ? hasFactsBody(event.facts) : !showsDetail || hasContextBody(event)
  const summary = event.facts ? factsSummary(event.facts) : event.context?.summary
  const actions: RowAction[] = [
    ...(onCommentInChat ? [{ label: 'Comment in chat', onClick: onCommentInChat }] : []),
    ...forwardActions(onForwardToCareTeam),
  ]

  return (
    <FeedRowCard
      timestamp={event.timestamp}
      backgroundColor={showsDetail && event.isCompleted ? advancedColors.surface : style.bg}
      actions={actions}
      header={
        <RowHeader
          icon={standardCategoryIcon(event.iconCategory)}
          color={style.fg}
          label={showsDetail ? event.label : `${labelForCategory(event.category, tracks)} activity`}
          summary={showsDetail ? summary : null}
          badge={
            showsDetail && event.facts?.kind === 'overdueQuestionnaire' ? (
              <Chip label="Overdue" color="error" size="small" sx={{ fontWeight: 600, flexShrink: 0 }} />
            ) : undefined
          }
          continued={showsDetail && event.continued}
        />
      }
    >
      {hasBody &&
        (event.facts ? (
          <ActivityFactsBody facts={event.facts} />
        ) : (
          <ActivityContextBody
            label={event.label}
            detail={event.detail}
            context={event.context}
            isCompleted={event.isCompleted}
          />
        ))}
    </FeedRowCard>
  )
}

type Props = {
  uid: string
  feed: ChatEventFeed
  onCommentInChat?: (comment: PendingComment) => void
  onEditMessage?: (message: EditingMessage) => void
  onForwardToCareTeam?: (item: ForwardedFeedItem) => void
}

const noop = (): void => undefined

const EventFeedViewBody = ({ uid, feed, onCommentInChat, onEditMessage, onForwardToCareTeam }: Props) => {
  const lastSeenAt = useLastSeenAt()
  const firstUnreadRef = useRef<HTMLDivElement>(null)
  const coachUid = useSessionUID()
  const { roleFor } = useCareTeamRoster(uid)
  const tracks = useTrackCatalog()
  const tourExample = useTourExample()
  const [role] = useCurrentRole()
  const viewerRole = roleCategoryIdFor(role)
  const example = tourExample === 'feed' && feed.events.length === 0
  const exampleEvents = useMemo(() => exampleFeedEvents(coachUid, viewerRole), [coachUid, viewerRole])
  const events = example ? exampleEvents : feed.events
  const hasOlder = !example && feed.hasOlder
  const [chosen, setAdded] = useState<ReadonlySet<string>>(new Set())
  const [messagesOnly, setMessagesOnly] = useState(false)

  const isAlwaysShown = useCallback(
    (category: string) =>
      !viewerRole ||
      category === 'system' ||
      category === 'patient' ||
      viewerOwnsCategory(category, viewerRole, tracks),
    [viewerRole, tracks]
  )

  const addOns = useMemo(
    () =>
      Array.from(new Set(events.filter((event) => event.kind !== 'chat').map((event) => event.category))).filter(
        (category) => !isAlwaysShown(category)
      ),
    [events, isAlwaysShown]
  )
  // Examples show every role's colour, as if each had been added from Filter.
  const added = example ? new Set(addOns) : chosen

  const visible = events.filter(
    (event) =>
      (event.kind === 'chat' || isAlwaysShown(event.category) || added.has(event.category)) &&
      (!messagesOnly || event.kind === 'chat')
  )

  const firstUnreadId = visible.find((event) => event.unread)?.id

  const toggle = (category: string) => {
    setAdded((current) => {
      const next = new Set(current)
      if (next.has(category)) {
        next.delete(category)
      } else {
        next.add(category)
      }
      return next
    })
  }

  const forwardToCareTeamFor = (item: ForwardedFeedItem): (() => void) | undefined => {
    if (example) {
      return noop
    }
    return onForwardToCareTeam ? () => onForwardToCareTeam(item) : undefined
  }
  const commentInChatFor = (comment: PendingComment | null): (() => void) | undefined => {
    if (!comment) {
      return undefined
    }
    if (example) {
      return noop
    }
    return onCommentInChat ? () => onCommentInChat(comment) : undefined
  }

  const renderRow = (event: FeedEvent): React.ReactNode => {
    switch (event.kind) {
      case 'chat':
        return (
          <ChatRow
            event={event}
            coachUid={coachUid}
            roleFor={roleFor}
            onEditMessage={example ? undefined : onEditMessage}
            onForwardToCareTeam={forwardToCareTeamFor({
              kind: 'chat',
              // A forward needs a label, and an attachment-only message has no text.
              label: event.text.trim() ? truncateForForward(event.text) : 'Sent an attachment',
              fromPatient: event.fromPatient,
            })}
          />
        )
      case 'activity': {
        return (
          <ActivityRow
            event={event}
            viewerRole={viewerRole}
            tracks={tracks}
            onCommentInChat={commentInChatFor(commentTarget(event))}
            onForwardToCareTeam={forwardToCareTeamFor(forwardedActivity(event))}
          />
        )
      }
      case 'history': {
        // Signed download URLs expire, so a forwarded snapshot carries no files.
        const entry = { ...event.entry, files: [] }
        return (
          <HistoryRow
            event={event}
            onForwardToCareTeam={
              fitsForward(entry)
                ? forwardToCareTeamFor({
                    kind: 'history',
                    label: labelForHistoryType(event.entry.type, valueOf(event.entry)),
                    entry,
                  })
                : undefined
            }
          />
        )
      }
      default: {
        const unhandled: never = event
        return unhandled
      }
    }
  }

  if (events.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: advancedColors.inkMuted, p: 1 }}>
        No events recorded yet.
      </Typography>
    )
  }

  return (
    <ExampleRowsProvider value={example}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <Box
          sx={{
            px: 1.25,
            height: CHAT_HEADER_HEIGHT,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            borderBottom: `1px solid ${advancedColors.line}`,
          }}
        >
          <FeedFilterMenu
            messagesOnly={messagesOnly}
            onToggleMessagesOnly={() => setMessagesOnly((current) => !current)}
            options={addOns}
            added={added}
            onToggle={toggle}
            tracks={tracks}
          />
        </Box>

        {/* The rotated watermark would otherwise enlarge the scroll height and carry this bar off screen. */}
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {example && <ExampleFeedNotice />}
          <ScrollableFeedContainer
            items={visible.map((event) => ({ itemID: event.id }))}
            firstUnreadRef={firstUnreadRef}
            unreadBoundaryKey={unreadBoundaryKey(lastSeenAt)}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {hasOlder && (
                <SeeMoreButton fetchMoreOlder={feed.fetchMoreOlder} isFetchingOlder={feed.isFetchingOlder} />
              )}
              {visible.length === 0 && (
                <Typography variant="body2" sx={{ color: advancedColors.inkMuted, textAlign: 'center', mt: 2 }}>
                  Nothing matches the current filter.
                </Typography>
              )}
              {visible.map((event, index) => {
                const previous = visible[index - 1]
                const startsDay = !previous || chatDayLabel(event.timestamp) !== chatDayLabel(previous.timestamp)
                const isFirstUnread = event.id === firstUnreadId

                return (
                  <React.Fragment key={event.id}>
                    {isFirstUnread && <div ref={firstUnreadRef} />}
                    {(startsDay || isFirstUnread) && (
                      <DayDivider
                        isNew={isFirstUnread}
                        isOnTop={hasOlder && index === 0}
                        label={startsDay ? chatDayLabel(event.timestamp) : undefined}
                      />
                    )}
                    {renderRow(event)}
                  </React.Fragment>
                )
              })}
            </Box>
          </ScrollableFeedContainer>
          {example && <ExampleFeedWatermark />}
        </Box>
      </Box>
    </ExampleRowsProvider>
  )
}

// The Feed isn't a dashboard panel, so the note bodies it reuses get their patient from here.
const EventFeedView = (props: Props) => (
  <PanelPatientProvider uid={props.uid}>
    <EventFeedViewBody {...props} />
  </PanelPatientProvider>
)

export default EventFeedView
